/**
 * Cart Upsell Variant Selection
 * Adds functionality to handle variant selection in cart upsell products
 */

(function() {
  // Set to true to enable debug logging, false to disable
  const DEBUG = false;
  
  // Logger function that only logs when DEBUG is true
  const log = (message, ...args) => {
    if (DEBUG) {
      console.log(message, ...args);
    }
  };
  
  // Store selected variants to persist across refreshes
  const STORAGE_KEY = 'cart_upsell_selected_variants';
  let selectedVariants = {};
  
  // Load any previously selected variants from localStorage
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      selectedVariants = JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading saved variants:', e);
  }
  
  // Helper function to dispatch custom events
  function dispatchCustomEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: detail
    });
    document.dispatchEvent(event);
    log(`Event dispatched: ${eventName}`, detail);
  }
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    initUpsellVariantSelectors();
  });
  
  // Also listen for cart drawer open events
  document.addEventListener('cart_drawer:opened', function() {
    setTimeout(initUpsellVariantSelectors, 100);
  });
  
  // When cart is refreshed, reinitialize
  document.addEventListener('cart:refresh', function() {
    // Use a longer timeout to ensure the cart drawer has been fully updated
    setTimeout(initUpsellVariantSelectors, 500);
  });
  
  // Also listen for cart drawer changes
  document.addEventListener('line-item:change:end', function() {
    // When cart items change (including removal), reinitialize with a delay
    setTimeout(initUpsellVariantSelectors, 500);
  });
  
  // Set up a default money format
  const DEFAULT_MONEY_FORMAT = '${{amount}}';
  
  /**
   * Initialize all variant selectors in the cart drawer
   */
  function initUpsellVariantSelectors() {
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (!cartDrawer) return;
    
    const upsellProducts = cartDrawer.querySelectorAll('.cart-upsell-product');
    if (!upsellProducts.length) return;
    
    log('Initializing upsell variants, products found:', upsellProducts.length);
    
    // For each upsell product
    upsellProducts.forEach(function(product) {
      const productId = product.dataset.productId;
      if (!productId) return;
      
      const selector = product.querySelector('[data-variant-selector]');
      if (!selector) return;
      
      const addButton = product.querySelector('.cart-upsell-add-to-cart');
      if (!addButton) return;
      
      // Debug log
      log(`Setting up product ${productId}, has saved variant: ${!!selectedVariants[productId]}`);
      
      // First make sure any initialized properties are cleared to prevent stale state
      selector.removeAttribute('data-initialized');
      if (addButton) {
        addButton.removeAttribute('data-initialized');
      }
      
      // Remove any existing event listeners by cloning and replacing
      const newSelector = selector.cloneNode(true);
      selector.parentNode.replaceChild(newSelector, selector);
      
      // Apply any previously selected variant
      if (selectedVariants[productId]) {
        const savedVariantId = selectedVariants[productId];
        const option = newSelector.querySelector(`option[value="${savedVariantId}"]`);
        
        if (option) {
          option.selected = true;
          
          // Update button's variant ID
          if (addButton) {
            addButton.setAttribute('data-variant-id', savedVariantId);
          }
          
          // Load product data and update price display
          loadProductAndUpdatePrice(newSelector, savedVariantId);
        }
      }
      
      // Mark the selector as initialized
      newSelector.setAttribute('data-initialized', 'true');
      
      // Add change event listener
      newSelector.addEventListener('change', handleVariantChange);
      log(`Event listener added for product ${productId}`);
    });
    
    // Notify that variants have been initialized
    dispatchCustomEvent('upsell:variants:initialized');
  }
  
  /**
   * Handle change event on variant selector
   */
  function handleVariantChange(event) {
    const selector = event.target;
    const selectedVariantId = selector.value;
    const productContainer = selector.closest('.cart-upsell-product');
    
    if (!productContainer) return;
    
    const productId = productContainer.dataset.productId;
    if (!productId) return;
    
    const addButton = productContainer.querySelector('.cart-upsell-add-to-cart');
    if (!addButton) return;
    
    // Save the selected variant
    selectedVariants[productId] = selectedVariantId;
    saveSelectedVariants();
    
    // Update the button's variant ID
    addButton.setAttribute('data-variant-id', selectedVariantId);
    
    // Update price display
    loadProductAndUpdatePrice(selector, selectedVariantId);
    
    // Remove the initialized flag from the button so it gets reinitialized
    if (addButton.hasAttribute('data-initialized')) {
      addButton.removeAttribute('data-initialized');
    }
    
    // Notify that a variant has been changed
    dispatchCustomEvent('upsell:variant:changed', {
      productId: productId,
      variantId: selectedVariantId
    });
  }
  
  /**
   * Save selected variants to localStorage
   */
  function saveSelectedVariants() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedVariants));
    } catch (e) {
      console.error('Error saving variants:', e);
    }
  }
  
  /**
   * Load product data and update price display
   */
  async function loadProductAndUpdatePrice(selector, variantId) {
    const productContainer = selector.closest('.cart-upsell-product');
    if (!productContainer) return;
    
    const productId = productContainer.dataset.productId;
    if (!productId) return;
    
    const handle = selector.dataset.handle;
    if (!handle) return;
    
    try {
      // Fetch product data
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`Error fetching product data: ${response.status}`);
      }
      
      const productData = await response.json();
      
      // Find the selected variant
      const selectedVariant = productData.variants.find(v => 
        v.id.toString() === variantId.toString()
      );
      
      if (!selectedVariant) {
        throw new Error(`Variant ${variantId} not found in product ${handle}`);
      }
      
      // Update price display
      updatePrice(productContainer, selectedVariant, productData);
      
      // Update button state
      const addButton = productContainer.querySelector('.cart-upsell-add-to-cart');
      if (addButton) {
        if (!selectedVariant.available) {
          addButton.disabled = true;
          const textSpan = addButton.querySelector('span');
          if (textSpan) textSpan.textContent = 'Out of Stock';
        } else {
          addButton.disabled = false;
          const textSpan = addButton.querySelector('span');
          if (textSpan) textSpan.textContent = 'Add';
        }
      }
      
    } catch (error) {
      console.error('Error updating price:', error);
    }
  }
  
  /**
   * Update the price display for the selected variant
   */
  function updatePrice(container, variant, productData) {
    // Try to find the price container - first by ID then by class
    let priceContainer = container.querySelector(`#price-container-${container.dataset.productId}`);
    if (!priceContainer) {
      priceContainer = container.querySelector('.price-container');
    }
    
    if (!priceContainer) {
      console.error('Price container not found for product:', container.dataset.productId);
      return;
    }
    
    // Debug log
    log('Updating price for variant:', variant.id, 'Price:', variant.price);
    
    // Get original price container to match format exactly
    const originalPrice = document.querySelector('.price.mb-2');
    const moneyFormat = originalPrice ? getComputedMoneyFormat(originalPrice) : DEFAULT_MONEY_FORMAT;
    
    // Format prices correctly (our formatMoney function now handles removing .00 automatically)
    const price = formatMoney(variant.price, moneyFormat);
    const compareAtPrice = variant.compare_at_price > 0 ? formatMoney(variant.compare_at_price, moneyFormat) : null;
    
    // Create the HTML structure - use flex layout to keep items on one line
    let html = '<div class="price !flex !items-center !flex-wrap upsell-price">';
    
    // Add compare at price if it exists and is greater than the price
    if (compareAtPrice && variant.compare_at_price > variant.price) {
      html += `<div class="mr-2"><span id="compare-at-price" class="amount line-through text-md">${compareAtPrice}</span></div>`;
    }
    
    // Add current price
    html += `<div class="mr-2"><span id="final-product-price" class="amount font-bold">${price}</span></div>`;
    
    // Add savings badge if there's a discount
    if (compareAtPrice && variant.compare_at_price > variant.price) {
      const savings = variant.compare_at_price - variant.price;
      // Format savings with our improved formatMoney function
      const formattedSavings = formatMoney(savings, moneyFormat);
      html += `<div class="savings-badge font-bold !text-[.9rem] bg-green-600 text-white px-2 py-0.5 rounded-sm whitespace-nowrap">Save ${formattedSavings}</div>`;
    }
    
    html += '</div>';
    
    // Update the price container directly
    priceContainer.innerHTML = html;
    
    // Log the update
    log('Price updated for product:', container.dataset.productId);
  }
  
  /**
   * Try to get the money format from an existing price element
   */
  function getComputedMoneyFormat(priceElement) {
    if (!priceElement) return DEFAULT_MONEY_FORMAT;
    
    // Find a price amount in the element
    const amountEl = priceElement.querySelector('.amount');
    if (!amountEl) return DEFAULT_MONEY_FORMAT;
    
    const priceText = amountEl.textContent.trim();
    // Extract the format by looking at how currency symbols and numbers are arranged
    if (priceText.match(/^\$\d/)) {
      return '${{amount}}'; // Starts with $
    } else if (priceText.match(/\d+\.\d{2}\s*USD$/i)) {
      return '{{amount}} USD'; // Ends with USD
    } else if (priceText.match(/^\d+\.\d{2}\$/)) {
      return '{{amount}}$'; // $ after amount
    }
    
    return DEFAULT_MONEY_FORMAT;
  }
  
  /**
   * Format money values correctly - removes .00 for whole dollar amounts but keeps decimals for cents
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace(/[^\d.]/g, '');
    }
    
    cents = parseFloat(cents);
    
    if (isNaN(cents) || cents === 0) {
      return '$0';
    }
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      
      if (isNaN(number) || number == null) {
        return 0;
      }
      
      // IMPORTANT: Check if we should remove decimal (when it's .00)
      // This checks if the number is a whole number with no cents
      const hasDecimals = number % 100 !== 0;
      
      // Use precision 0 for whole dollars, 2 for amounts with cents
      precision = hasDecimals ? 2 : 0;
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';
      
      return dollarsAmount + centsAmount;
    }
    
    const formatString = format || DEFAULT_MONEY_FORMAT;
    
    // Use our modified formatter for all price formats
    // This will automatically handle removing .00 while keeping other decimals
    return formatString
      .replace(/\{\{amount\}\}/g, formatWithDelimiters(cents))
      .replace(/\{\{amount_no_decimals\}\}/g, formatWithDelimiters(cents, 0))
      .replace(/\{\{amount_with_comma_separator\}\}/g, formatWithDelimiters(cents, null, '.', ','))
      .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, formatWithDelimiters(cents, 0, '.', ','))
      .replace(/\{\{amount_with_apostrophe_separator\}\}/g, formatWithDelimiters(cents, null, '\'', '.'))
      .replace(/\{\{amount_with_space_separator\}\}/g, formatWithDelimiters(cents, null, ' ', '.'));
  }
})(); 